import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';

import { RoutePath } from '@components/AppRouter/constants';
import { NEW_ENTITY_ID } from '@components/AppRouter/hooks/constants';
import { cmApi } from '@src/api';
import { usePermission } from '@src/utils/hooks/usePermission';
import { objectFieldsToCamelCase } from '@src/utils/objectFieldsToCamelCase';
import { confirmDialogStore } from '@stores/confirmDialog';
import { controlGroupsStore } from '@stores/dictionaryStores/controlGroupsStore/controlGroupsStore';
import { preloaderStore } from '@stores/preloaderStore';
import type { ControlGroupDtoDefId } from '@stores/dictionaryStores/controlGroupsStore/controlGroupsStore';

export const ActionButton = observer(({ update }: { update: () => void }) => {
  const { push } = useHistory();
  const { groupDelete, checked, setChecked, fetch } = controlGroupsStore;
  const { open: openConfirmDialog } = confirmDialogStore;

  const permission = usePermission('CmGroupControl');

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROL_GROUPS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const onEdit = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROL_GROUPS}/${checked[0]?.ctrl_group_code}`);
  }, [checked, push]);

  const onDelete = useCallback(() => {
    openConfirmDialog({
      text: `Удалить выбранные элементы (${checked.length})?`,
      onOk: () => {
        const items = checked.map((item) => objectFieldsToCamelCase(item));

        groupDelete(items as ControlGroupDtoDefId[]).then(() => {
          setChecked([]);
          update();
        });
      },
    });
  }, [checked, groupDelete, openConfirmDialog, setChecked, update]);

  const onSwap = useCallback(async () => {
    preloaderStore.preloader(true);

    const out = [
      {
        ctrlGroupCode: checked[0].ctrl_group_code,
        ctrlGroupName: checked[0].ctrl_group_name,
        ctrlGroupOrder: checked[1].ctrl_group_order,
      },
      {
        ctrlGroupCode: checked[1].ctrl_group_code,
        ctrlGroupName: checked[1].ctrl_group_name,
        ctrlGroupOrder: checked[0].ctrl_group_order,
      },
    ];

    try {
      const promises = out.map((item) => cmApi.controlGroups.updateControlGroupByCode(objectFieldsToCamelCase(item)));
      await Promise.all(promises);
      await fetch();
      update();
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  }, [checked, fetch, update]);

  return (
    <>
      {permission.CREATE && <IconButton onClick={onCreate} iconName="add" title="Добавить" />}
      {permission.UPDATE && (
        <IconButton onClick={onEdit} disabled={checked.length !== 1} iconName="edit" title="Редактировать" />
      )}
      {permission.DELETE && (
        <IconButton onClick={onDelete} disabled={checked.length === 0} iconName="delete" title="Удалить" />
      )}
      {permission.UPDATE && (
        <IconButton onClick={onSwap} iconName="refresh" title="Поменять номера шагов" disabled={checked.length !== 2} />
      )}
    </>
  );
});
